import { DirectionsRun, Login as LoginIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useEntraAuth } from '../../contexts/EntraAuthContext';
import { logger } from '../../utils/logger';

/**
 * Entra ID Sign In Component
 *
 * This is the primary authentication page for PacePilot.
 * Users must sign in with their Microsoft account before accessing the app.
 */
const EntraSignIn: React.FC = () => {
  const { login, isAuthenticated, user } = useEntraAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async () => {
    try {
      setError(null);
      setIsLoading(true);
      await login();
    } catch (err) {
      logger.error('Sign in error', err);
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show signed in state (this shouldn't normally be visible as they'd be redirected)
  if (isAuthenticated && user) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          py={4}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Box textAlign="center">
              <Typography variant="h5" gutterBottom>
                Welcome, {user.name}!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.username}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        py={4}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box textAlign="center" mb={4}>
            <DirectionsRun sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" component="h1" gutterBottom>
              PacePilot
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your Personal Running Training Assistant
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box mb={3}>
            <Typography variant="body1" align="center" color="text.secondary" gutterBottom>
              Sign in to get started
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box mb={3}>
            <Button
              variant="contained"
              size="large"
              startIcon={isLoading ? <CircularProgress size={20} /> : <LoginIcon />}
              onClick={handleSignIn}
              disabled={isLoading}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" align="center" color="text.secondary" gutterBottom>
              After signing in, you'll be able to:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Connect your Intervals.icu account for training data
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Access personalized training plans
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Track your progress and fitness metrics
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                View your training calendar
              </Typography>
            </Box>
          </Box>

          <Box mt={4}>
            <Typography variant="caption" align="center" display="block" color="text.secondary">
              New to PacePilot? Signing in will automatically create your account.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default EntraSignIn;
